// Admin Rate Estimator — multi-carrier shipping-rate quoting.
//
// Two URL-driven sub-tabs (mirrors the Tracking.jsx tab pattern):
//
//   /admin/rate-estimator         → Single (default) — one shipment → live
//                                    quotes per connected carrier, showing BOTH
//                                    the raw carrier cost and the marked-up
//                                    client price.
//   /admin/rate-estimator/bulk    → Bulk — download an Excel template, fill
//                                    rows, upload → download a costed workbook.
//
// Admin picks the client (markup is per-client) via getAdminClients. On the
// Single tab the Ship From origin is editable, defaulting to the fixed OPS 3PL
// Rosebery warehouse.

import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Calculator, FileSpreadsheet, Download, Upload, Loader2 } from 'lucide-react'
import RateQuoteForm from '../../components/rate-estimator/RateQuoteForm'
import RateResultsTable from '../../components/rate-estimator/RateResultsTable'
import { ToastContainer, useToast } from '../../components/Toast'
import {
  getAdminClients,
  postAdminRateQuote,
  downloadRateTemplate,
  postAdminBulkRates,
} from '../../lib/api'

const TABS = [
  { key: 'single', label: 'Single', icon: Calculator },
  { key: 'bulk',   label: 'Bulk',   icon: FileSpreadsheet },
]
const DEFAULT_TAB = 'single'

// Shared client selector — markup is per-client so a client MUST be chosen
// before any quote can be run on either sub-tab.
function ClientSelect({ value, onChange, clients, loading }) {
  return (
    <div>
      <label className="label">Client</label>
      <select
        className="input max-w-md"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={loading}
      >
        <option value="">Select a client…</option>
        {clients.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  )
}

function SingleTab({ clients, clientsLoading, addToast }) {
  const [clientId, setClientId] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(payload) {
    if (!clientId) {
      addToast({ type: 'warning', message: 'Pick a client first — markup is per-client.' })
      return
    }
    setLoading(true)
    setResult(null)
    try {
      const data = await postAdminRateQuote({ client_id: clientId, ...payload })
      setResult(data)
      if ((data.rates || []).length === 0 && (data.errors || []).length === 0) {
        addToast({ type: 'info', message: 'No rates returned for this shipment.' })
      }
    } catch (err) {
      addToast({
        type: 'error',
        message: err.response?.data?.detail || err.message || 'Failed to fetch rates.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="card p-5 space-y-5">
        <ClientSelect
          value={clientId}
          onChange={(v) => { setClientId(v); setResult(null) }}
          clients={clients}
          loading={clientsLoading}
        />
        <div className="border-t border-slate-100 pt-5">
          <RateQuoteForm
            onSubmit={handleSubmit}
            loading={loading}
            editableOrigin
            disabled={!clientId}
          />
        </div>
      </div>

      {result && (
        <RateResultsTable
          rates={result.rates || []}
          errors={result.errors || []}
          showCost
        />
      )}
    </div>
  )
}

function BulkTab({ clients, clientsLoading, addToast }) {
  const [clientId, setClientId] = useState('')
  const [file, setFile] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [uploading, setUploading] = useState(false)

  async function handleTemplate() {
    setDownloading(true)
    try {
      await downloadRateTemplate()
    } catch (err) {
      addToast({
        type: 'error',
        message: err.response?.data?.detail || err.message || 'Failed to download template.',
      })
    } finally {
      setDownloading(false)
    }
  }

  async function handleUpload() {
    if (!clientId) {
      addToast({ type: 'warning', message: 'Pick a client first — markup is per-client.' })
      return
    }
    if (!file) {
      addToast({ type: 'warning', message: 'Choose a filled template to upload.' })
      return
    }
    setUploading(true)
    try {
      await postAdminBulkRates(clientId, file)
      addToast({ type: 'success', message: 'Costing workbook downloaded.' })
    } catch (err) {
      addToast({
        type: 'error',
        message: err.response?.data?.detail || err.message || 'Bulk quoting failed.',
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="card p-5 space-y-5 max-w-2xl">
      <ClientSelect
        value={clientId}
        onChange={setClientId}
        clients={clients}
        loading={clientsLoading}
      />

      {/* Step 1 — template */}
      <div className="border-t border-slate-100 pt-5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">1 — Download template</p>
        <p className="text-sm text-slate-500 mb-3">
          Grab the blank workbook, fill one row per shipment, then upload it below.
        </p>
        <button className="btn-secondary" onClick={handleTemplate} disabled={downloading}>
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Download template
        </button>
      </div>

      {/* Step 2 — upload */}
      <div className="border-t border-slate-100 pt-5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">2 — Upload & cost</p>
        <p className="text-sm text-slate-500 mb-3">
          Each row is quoted across every connected carrier and marked up for the
          selected client. You'll get back a workbook with the cheapest carrier
          plus every carrier's price per row.
        </p>
        <input
          type="file"
          accept=".xlsx"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 mb-3"
        />
        <button className="btn-primary" onClick={handleUpload} disabled={uploading || !file || !clientId}>
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? 'Quoting rows…' : 'Upload & download results'}
        </button>
      </div>
    </div>
  )
}

export default function AdminRateEstimator() {
  const navigate = useNavigate()
  const { tab: tabParam } = useParams()
  const activeKey = TABS.find(t => t.key === tabParam)?.key ?? DEFAULT_TAB
  const { toasts, addToast, removeToast } = useToast()

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['admin', 'clients', 'rate-estimator'],
    queryFn: () => getAdminClients(),
    staleTime: 5 * 60_000,
  })
  // Sort alphabetically for the picker — the totals-ordered default isn't
  // meaningful here.
  const clients = useMemo(
    () => [...(clientsData || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clientsData],
  )

  return (
    <div className="p-4 sm:p-6 max-w-[1200px] mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-800">Rate Estimator</h1>
        <p className="text-sm text-slate-500 mt-1">
          Live multi-carrier shipping quotes with the client's markup applied.
          You see the raw carrier cost and the marked-up client price.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-slate-200 mb-5">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {TABS.map(t => {
            const Icon = t.icon
            const isActive = t.key === activeKey
            return (
              <button
                key={t.key}
                onClick={() => navigate(
                  t.key === DEFAULT_TAB ? '/admin/rate-estimator' : `/admin/rate-estimator/${t.key}`
                )}
                className={[
                  'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                ].join(' ')}
              >
                <Icon size={15} />
                {t.label}
              </button>
            )
          })}
        </nav>
      </div>

      {activeKey === 'single'
        ? <SingleTab clients={clients} clientsLoading={clientsLoading} addToast={addToast} />
        : <BulkTab   clients={clients} clientsLoading={clientsLoading} addToast={addToast} />}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}
